import { CheckCircle2, Circle } from 'lucide-react';
import Button from '../../ui/Button';
import { cn } from '../../../utils/cn';
import { formatDateTimeInTz } from '../../../utils/timezone';

/**
 * A quarantine treatment's customer orders, each signed off as treated by
 * whoever applied it (Rob, 2026-10-14). A treatment is not validated until
 * every order still on the load is signed off, so despatch waits on each one.
 * Shown on the spray's page, where the operator works, and on the load's
 * Quarantine card.
 */

export interface SignoffRow {
  order_id: number;
  order_number: string;
  customer_name: string | null;
  plants?: number;
  /** Taken off the load or cancelled since: nothing to sign. */
  gone?: boolean;
  signed_at: string | null;
  signed_by_name: string | null;
}

export function QuarantineOrderSignoffs({ rows, tz, canSign, busyOrderId, onSign, onUndo }: {
  rows: SignoffRow[];
  tz: string;
  /** The spray is under way, the treatment not validated, and the reader has a spray seat. */
  canSign: boolean;
  busyOrderId: number | null;
  onSign: (orderId: number) => void;
  onUndo: (orderId: number) => void;
}) {
  return (
    <ul className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg">
      {rows.map((o) => {
        const busy = busyOrderId === o.order_id;
        return (
          <li key={o.order_id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              {o.signed_at
                ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" aria-label="Treated" />
                : <Circle className={cn('h-5 w-5 flex-shrink-0', o.gone ? 'text-neutral-300' : 'text-amber-500')} aria-label="Not signed off" />}
              <div className="min-w-0">
                <p className="text-sm font-medium text-neutral-900 break-words">
                  {[o.order_number, o.customer_name].filter(Boolean).join(' · ')}
                </p>
                <p className="text-xs text-neutral-500 break-words">
                  {[
                    o.plants != null ? `${o.plants.toLocaleString()} plant${o.plants === 1 ? '' : 's'}` : null,
                    o.signed_at
                      ? `Treated, signed off by ${o.signed_by_name || 'someone'} ${formatDateTimeInTz(o.signed_at, tz)}`
                      : o.gone ? 'No longer on the load: nothing to sign' : 'Not signed off yet',
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            {canSign && !o.gone && (
              o.signed_at ? (
                <Button variant="outline" size="sm" disabled={busy} onClick={() => onUndo(o.order_id)}>
                  {busy ? 'Undoing…' : 'Undo'}
                </Button>
              ) : (
                <Button variant="primary" size="sm" disabled={busy} onClick={() => onSign(o.order_id)}>
                  {busy ? 'Signing off…' : 'Treated'}
                </Button>
              )
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default QuarantineOrderSignoffs;
